import React from 'react';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Switch } from './ui/switch';
import { Plus, Trash2, ListChecks } from 'lucide-react';

const newOpt = () => ({ id: `tmp-${Math.random().toString(36).slice(2)}`, label: '', price: '' });

export const PriceOptionsEditor = ({ enabled, options, onChange }) => {
  const opts = options || [];
  const setOpts = (next) => onChange({ enabled, options: next });
  const update = (i, patch) => setOpts(opts.map((o, idx) => (idx === i ? { ...o, ...patch } : o)));

  return (
    <div className="rounded-lg border border-border p-3 space-y-3 bg-accent/20" data-testid="price-options-editor">
      <div className="flex items-center justify-between gap-2">
        <Label className="flex items-center gap-2 font-bold">
          <ListChecks className="h-4 w-4 text-primary" /> خيارات أسعار متعددة
        </Label>
        <Switch
          checked={!!enabled}
          onCheckedChange={(v) => onChange({ enabled: v, options: v && opts.length === 0 ? [newOpt(), newOpt()] : opts })}
          data-testid="price-options-toggle"
        />
      </div>
      {!enabled ? (
        <p className="text-xs text-foreground/55">غير مفعّل — يُعتمد السعر الأساسي أعلاه.</p>
      ) : (
        <div className="space-y-2">
          {opts.map((o, i) => (
            <div key={o.id} className="flex items-center gap-2" data-testid={`price-option-row-${i}`}>
              <Input
                value={o.label}
                onChange={(e) => update(i, { label: e.target.value })}
                placeholder="الوصف (مثال: كرتون 5 كيلو)"
                className="flex-1 h-9 text-sm"
                data-testid={`price-option-label-${i}`}
              />
              <Input
                type="number"
                step="0.01"
                min="0"
                value={o.price}
                onChange={(e) => update(i, { price: e.target.value })}
                placeholder="السعر"
                className="w-24 h-9 text-sm font-numbers"
                data-testid={`price-option-price-${i}`}
              />
              <button
                type="button"
                onClick={() => setOpts(opts.filter((_, idx) => idx !== i))}
                className="h-9 w-9 shrink-0 inline-flex items-center justify-center rounded-md text-red-600 hover:bg-red-50 transition-colors"
                data-testid={`price-option-remove-${i}`}
              >
                <Trash2 className="h-4 w-4" />
              </button>
            </div>
          ))}
          <button
            type="button"
            onClick={() => setOpts([...opts, newOpt()])}
            className="w-full h-9 inline-flex items-center justify-center gap-1.5 rounded-md border border-dashed border-primary/50 text-primary text-sm font-bold hover:bg-primary/5 transition-colors"
            data-testid="price-option-add"
          >
            <Plus className="h-4 w-4" /> إضافة خيار
          </button>
          <p className="text-[11px] text-foreground/55">عند البيع يختار الموظف أحد الخيارات ويُعبأ سعره تلقائياً.</p>
        </div>
      )}
    </div>
  );
};

export const hasPriceOptions = (p) => !!(p && p.price_options_enabled && (p.price_options || []).length > 0);

export const PriceOptionPicker = ({ product, value, values, onSelect, testId, small = false }) => {
  if (!hasPriceOptions(product)) return null;
  const multiple = Array.isArray(values);
  return (
    <div className="space-y-1.5" data-testid={testId}>
      <Label className={small ? 'text-sm' : ''}>
        اختر الصنف / السعر
        {multiple && <span className="text-foreground/50 text-[11px] font-normal"> (يمكن اختيار أكثر من صنف)</span>}
      </Label>
      <div className={small ? 'grid grid-cols-2 gap-1.5' : 'flex flex-wrap gap-2'}>
        {product.price_options.map((o) => {
          const active = multiple ? values.includes(o.label) : value === o.label;
          return (
            <button
              key={o.id}
              type="button"
              onClick={() => onSelect(o)}
              className={`${small ? 'px-2 py-1.5 rounded-md text-[11px] flex items-center justify-between gap-1 min-w-0' : 'px-3 py-2 rounded-lg text-sm'} border font-bold transition-colors ${
                active ? 'bg-primary text-white border-primary shadow-sm' : 'bg-white border-border hover:bg-accent/40'
              }`}
              data-testid={`${testId}-opt-${o.id}`}
            >
              {small ? (
                <>
                  <span className="truncate">{o.label}</span>
                  <span className="font-numbers shrink-0 opacity-90">{Number(o.price || 0).toLocaleString('en-US')}</span>
                </>
              ) : (
                <>{o.label} — <span className="font-numbers">{Number(o.price || 0).toLocaleString('en-US')}</span> ر.س</>
              )}
            </button>
          );
        })}
      </div>
    </div>
  );
};
